import React from 'react';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Users, Award, TrendingUp, Briefcase, Star, Quote } from 'lucide-react';

const PlacementsPage = () => {
  const navigate = useNavigate();

  const placementStats = [
    {
      icon: Users,
      value: '20,000+',
      label: 'Total Placements',
      color: 'blue'
    },
    {
      icon: Award,
      value: '85%',
      label: 'Placement Rate',
      color: 'emerald'
    },
    {
      icon: Briefcase,
      value: '500+',
      label: 'Partner Companies',
      color: 'purple'
    },
    {
      icon: TrendingUp,
      value: '₹15-25k',
      label: 'Average Starting Salary',
      color: 'orange'
    }
  ];

  const testimonials = [
    {
      name: 'Rahul Kumar',
      course: 'Digital Marketing',
      company: 'Tech Solutions Pvt Ltd',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
      video: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      text: 'Iqra Skills transformed my career! The practical training and placement support helped me land my dream job in digital marketing. The trainers are highly experienced and supportive.'
    },
    {
      name: 'Priya Sharma',
      course: 'Financial Accounting',
      company: 'ABC Consultants',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330',
      video: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      text: 'The Financial Accounting course gave me all the skills I needed. Within 2 weeks of completing the course, I got placed in a leading consultancy firm. Thank you Iqra Skills!'
    },
    {
      name: 'Amit Verma',
      course: 'AutoCAD',
      company: 'Design Studio Pro',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e',
      video: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      text: 'Excellent training infrastructure and knowledgeable faculty. The hands-on projects prepared me well for the industry. Highly recommend Iqra Skills for skill development.'
    },
    {
      name: 'Sneha Patel',
      course: 'Data Entry Operator',
      company: 'InfoTech Services',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80',
      video: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      text: 'From zero computer knowledge to getting placed in IT company - Iqra Skills made it possible. The course was well-structured and the placement team was very helpful.'
    },
    {
      name: 'Vikash Singh',
      course: 'CIVIL CAD',
      company: 'Construction Corp',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e',
      video: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      text: 'Best decision to join Iqra Skills for CIVIL CAD. The practical training on real projects and industry exposure helped me secure a great position. Forever grateful!'
    },
    {
      name: 'Anjali Gupta',
      course: 'Web Designer',
      company: 'Digital Creators',
      image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f',
      video: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      text: 'The web design course exceeded my expectations. Modern curriculum, experienced trainers, and excellent placement support. I got my first client even before completing the course!'
    }
  ];

  const topRecruiters = [
    'TCS', 'Infosys', 'Wipro', 'Tech Mahindra', 'HCL', 'IBM',
    'Accenture', 'Cognizant', 'Capgemini', 'L&T', 'Reliance',
    'Amazon', 'Flipkart', 'Paytm', 'HDFC Bank', 'ICICI Bank'
  ];

  const galleryImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_skill-india-partner/artifacts/cuy9j468_505990147_30182843594662803_500540383002065739_n.jpg',
      caption: 'Training Session in Progress'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_skill-india-partner/artifacts/9e4sfpnk_499029090_29866837612930071_5496693785407116872_n.jpg',
      caption: 'Award Ceremony & Recognition'
    },
    {
      url: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644',
      caption: 'Computer Lab Training'
    },
    {
      url: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f',
      caption: 'Practical Workshop Session'
    },
    {
      url: 'https://images.unsplash.com/photo-1531482615713-2afd69097998',
      caption: 'Students Working on Projects'
    },
    {
      url: 'https://images.unsplash.com/photo-1552664730-d307ca884978',
      caption: 'Group Discussion & Learning'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="hero-gradient py-20" data-testid="placements-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Placements & <span className="text-blue-600">Success Stories</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            20,000+ successful placements across leading companies in India
          </p>
        </div>
      </section>

      {/* Placement Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Our Placement Record
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {placementStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center bg-slate-50 rounded-xl p-8 border border-slate-200">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-${stat.color}-100 rounded-full mb-4`}>
                    <Icon className={`h-8 w-8 text-${stat.color}-600`} />
                  </div>
                  <h3 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stat.value}
                  </h3>
                  <p className="text-slate-600 font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Success Stories from Our Students
          </h2>
          <p className="text-center text-slate-600 mb-12">Hear directly from our successful candidates</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl border border-slate-200 overflow-hidden card-hover">
                {/* Video Thumbnail */}
                <div className="relative aspect-video bg-slate-200">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <button className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                      <svg className="w-8 h-8 text-blue-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Testimonial Content */}
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover mr-3"
                    />
                    <div>
                      <h3 className="font-bold text-slate-900">{testimonial.name}</h3>
                      <p className="text-sm text-slate-600">{testimonial.course}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <Quote className="h-6 w-6 text-blue-600 mb-2" />
                    <p className="text-slate-700 text-sm italic">{testimonial.text}</p>
                  </div>
                  
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-sm font-semibold text-emerald-600">
                      Placed at: {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Recruiters */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Our Top Recruiters
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
            {topRecruiters.map((company, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-4 flex items-center justify-center border border-slate-200 hover:border-blue-600 transition-colors">
                <span className="font-semibold text-slate-700 text-center text-sm">{company}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Training Center Gallery
          </h2>
          <p className="text-center text-slate-600 mb-12">Glimpses from our training sessions and celebrations</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div key={index} className="relative group overflow-hidden rounded-xl shadow-lg">
                <img 
                  src={image.url}
                  alt={image.caption}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                  <p className="text-white font-semibold">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Start Your Success Story?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join 20,000+ successful candidates who transformed their careers with Iqra Skills
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              onClick={() => navigate('/register')} 
              className="bg-white text-blue-600 hover:bg-slate-100 rounded-lg px-8 py-6 text-lg"
              data-testid="cta-register"
            >
              Register Now
            </Button>
            <Button 
              onClick={() => navigate('/contact')} 
              variant="outline"
              className="border-2 border-white text-white hover:bg-blue-700 rounded-lg px-8 py-6 text-lg"
              data-testid="cta-contact"
            >
              Talk to Career Counselor
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default PlacementsPage;
